import { Icon } from '../../../../widgets/icon';
import { showErrorNotification } from '../../../../widgets/notificationToaster';
import React, { FC, useCallback, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import BoxOpenIcon from 'src/shared/icons/box-open-solid.svg';
import CloudUploadIcon from 'src/shared/icons/cloud-upload-alt-solid.svg';
import FolderOpenIcon from 'src/shared/icons/folder-open-solid.svg';
import InfoCircleIcon from 'src/shared/icons/info-circle-solid.svg';
import styled, { keyframes } from 'styled-components';
import { MAX_UPLOAD_SIZE_MB } from '../../constants';
import { getImageDimensions } from '../../utils/imageHelpers';
import { HiddenInput } from './sharedStyles';

/* ─── Styled components ──────────────────────────────────────────────────── */

const SplitUploadContainer = styled.div`
    flex: 1;
    display: flex;
    align-items: stretch;
    gap: 8px;
`;

const UploadZoneHalf = styled.button<{ $isDragOver?: boolean; $isLoading?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    border: 2px dashed ${(props) => (props.$isDragOver ? '#3b82f6' : '#d1d5db')};
    border-radius: 12px;
    background: ${(props) => (props.$isDragOver ? 'rgba(59, 130, 246, 0.1)' : 'transparent')};
    cursor: ${(props) => (props.$isLoading ? 'default' : 'pointer')};
    transition: all 0.2s ease;

    &:hover {
        border-color: ${(props) => (props.$isLoading ? '#d1d5db' : '#3b82f6')};
        background: ${(props) => (props.$isLoading ? 'transparent' : 'rgba(59, 130, 246, 0.08)')};
    }

    &:disabled {
        pointer-events: none;
    }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const UploadSpinner = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    animation: ${spin} 0.7s linear infinite;
`;

const UploadZoneIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #60a5fa 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;

    ${Icon} {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

const UploadZoneText = styled.div`
    text-align: center;
`;

const UploadZoneTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #1d4ed8;
    margin-bottom: 1px;
`;

const UploadZoneHint = styled.div`
    font-size: 11px;
    color: #60a5fa;
`;

const OrSeparator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 500;
    color: #9ca3af;
    flex-shrink: 0;
`;

const UploadInfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    line-height: 1.4;
    position: relative;
    cursor: pointer;
    ${Icon} {
        width: 12px;
        height: 12px;
        fill: #3b82f6;
        flex-shrink: 0;
    }
`;

const UploadInfoLabel = styled.span`
    font-size: 11px;
    color: #3b82f6;
    font-weight: 500;
`;

const UploadInfoTooltip = styled.div<{ $visible: boolean }>`
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    width: 220px;
    background: #ffffff;
    border: 1px solid #dbeafe;
    border-radius: 16px;
    box-shadow:
        0 8px 32px rgba(59, 130, 246, 0.14),
        0 2px 8px rgba(0, 0, 0, 0.06);
    padding: 16px;
    z-index: 300;
    pointer-events: none;
    opacity: ${(props) => (props.$visible ? 1 : 0)};
    transition: opacity 0.16s ease;
`;

const UploadInfoTooltipDesc = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
`;

/* ─── Catalog (Zakeke) zone — purple (same as library) ────────────────── */

const CatalogZone = styled.button`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    border: 2px dashed #c4b5fd;
    border-radius: 12px;
    background: rgba(124, 58, 237, 0.03);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #7c3aed;
        background: rgba(124, 58, 237, 0.08);
    }
`;

const CatalogZoneIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;

    ${Icon} {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
`;

const CatalogZoneTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #7c3aed;
    margin-bottom: 1px;
`;

const CatalogZoneHint = styled.div`
    font-size: 11px;
    color: #a78bfa;
`;

/* ─── Library zone — purple (unchanged) ────────────────────────────────── */

const LibraryZone = styled.button`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    border: 2px dashed #c4b5fd;
    border-radius: 12px;
    background: rgba(124, 58, 237, 0.03);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #7c3aed;
        background: rgba(124, 58, 237, 0.08);
    }
`;

const LibraryZoneIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;

    ${Icon} {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

const LibraryZoneTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #7c3aed;
    margin-bottom: 1px;
`;

const LibraryZoneHint = styled.div`
    font-size: 11px;
    color: #a78bfa;
`;

/* ─── Component ──────────────────────────────────────────────────────────── */

interface ImageUploadZonesProps {
    onImageUpload: (file: File) => void;
    onOpenCatalog?: () => void;
    onOpenLibrary?: () => void;
    supportsMultiple?: boolean;
    maxSizeMB?: number;
    /** When set, the uploaded image is rejected if either dimension exceeds this many pixels. */
    maxDimension?: number;
}

export const ImageUploadZones: FC<ImageUploadZonesProps> = ({
    onImageUpload,
    onOpenCatalog,
    onOpenLibrary,
    supportsMultiple = false,
    maxSizeMB = MAX_UPLOAD_SIZE_MB,
    maxDimension
}) => {
    const inputFileRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragOver(false), []);

    const startUpload = useCallback(
        async (file: File) => {
            if (file.size > maxSizeBytes) {
                showErrorNotification({ message: T._(`File size must not exceed ${maxSizeMB} MB.`, 'Admin') });
                return;
            }
            if (maxDimension) {
                try {
                    const { width, height } = await getImageDimensions(file);
                    if (width > maxDimension || height > maxDimension) {
                        showErrorNotification({
                            message: T._(`Image resolution must not exceed ${maxDimension}x${maxDimension} pixels.`, 'Admin')
                        });
                        return;
                    }
                } catch {
                    showErrorNotification({ message: T._('Unable to read image. Please try a different file.', 'Admin') });
                    return;
                }
            }
            setIsUploading(true);
            window.setTimeout(() => {
                onImageUpload(file);
            }, 500);
        },
        [onImageUpload, maxSizeBytes, maxSizeMB, maxDimension]
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            if (isUploading) return;
            const file = e.dataTransfer.files?.[0];
            if (file?.type.startsWith('image/')) startUpload(file);
        },
        [isUploading, startUpload]
    );

    const handleInputFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) startUpload(file);
            e.target.value = '';
        },
        [startUpload]
    );

    return (
        <>
            <SplitUploadContainer>
                <UploadZoneHalf
                    onClick={() => !isUploading && inputFileRef.current?.click()}
                    $isDragOver={isDragOver}
                    $isLoading={isUploading}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    disabled={isUploading}
                    type='button'
                >
                    {isUploading ? (
                        <UploadSpinner />
                    ) : (
                        <>
                            <UploadZoneIcon>
                                <Icon>
                                    <CloudUploadIcon />
                                </Icon>
                            </UploadZoneIcon>
                            <UploadZoneText>
                                <UploadZoneTitle>
                                    {supportsMultiple ? T._('Drop images or click to upload', 'Admin') : T._('Drop image or click to upload', 'Admin')}
                                </UploadZoneTitle>
                                <UploadZoneHint>{T._(`PNG, JPG, WEBP up to ${maxSizeMB}MB`, 'Admin')}</UploadZoneHint>
                            </UploadZoneText>
                            <UploadInfoRow
                                onMouseEnter={() => setTooltipVisible(true)}
                                onMouseLeave={() => setTooltipVisible(false)}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Icon>
                                    <InfoCircleIcon />
                                </Icon>
                                <UploadInfoLabel>{T._('Image upload suggestions', 'Admin')}</UploadInfoLabel>
                                <UploadInfoTooltip $visible={tooltipVisible}>
                                    <UploadInfoTooltipDesc>
                                        {T._(
                                            `Maximum file size of ${maxSizeMB} MB and a minimum resolution of 128x128, and a maximum resolution of 4096x4096.`,
                                            'Admin'
                                        )}
                                    </UploadInfoTooltipDesc>
                                </UploadInfoTooltip>
                            </UploadInfoRow>
                        </>
                    )}
                </UploadZoneHalf>

                {onOpenCatalog && (
                    <>
                        <OrSeparator>{T._('or', 'Admin')}</OrSeparator>
                        <CatalogZone onClick={onOpenCatalog} type='button'>
                            <CatalogZoneIcon>
                                <Icon>
                                    <BoxOpenIcon />
                                </Icon>
                            </CatalogZoneIcon>
                            <UploadZoneText>
                                <CatalogZoneTitle>{T._('Start with a Zakeke product', 'Admin')}</CatalogZoneTitle>
                                <CatalogZoneHint>{T._('customize or position it before AI generation', 'Admin')}</CatalogZoneHint>
                            </UploadZoneText>
                        </CatalogZone>
                    </>
                )}

                {onOpenLibrary && (
                    <>
                        <OrSeparator>{T._('or', 'Admin')}</OrSeparator>
                        <LibraryZone onClick={onOpenLibrary} type='button'>
                            <LibraryZoneIcon>
                                <Icon>
                                    <FolderOpenIcon />
                                </Icon>
                            </LibraryZoneIcon>
                            <UploadZoneText>
                                <LibraryZoneTitle>{T._('Select from library', 'Admin')}</LibraryZoneTitle>
                                <LibraryZoneHint>{T._('Choose from your saved assets', 'Admin')}</LibraryZoneHint>
                            </UploadZoneText>
                        </LibraryZone>
                    </>
                )}
            </SplitUploadContainer>
            <HiddenInput ref={inputFileRef} type='file' accept='image/*' multiple={supportsMultiple} onChange={handleInputFileChange} />
        </>
    );
};

export default ImageUploadZones;
